import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Divider, TextField, Alert, Tooltip } from "@mui/material";
import {
  Percent,
  Visibility,
  InfoOutlined,
  Calculate,
  CheckCircle,
} from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { useSelector } from "react-redux";
import { CustomAutoComplete } from "components/CustomComponents";
import {
  CustomButton,
  CustomButtonWithBorder,
} from "components/CustomComponents/CustomButton";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { infoStyle, toAlignEnd, themes } from "services/constants";

const roundingRuleOptions = [
  { label: "Exact Calculation (Recommended)", value: "none" },
  { label: "Nearest", value: "nearest" },
  { label: "Round Up (Ceiling)", value: "ceiling" },
  { label: "Round Down (Floor)", value: "floor" },
];

const roundingRuleDescriptions = {
  none:
    "Exact Calculation preserves the full precision of the computed amount — no rounding is applied.",
  nearest:
    "The calculated amount will be rounded to the nearest multiple of the selected value.",
  ceiling:
    "The calculated amount will always be rounded up (ceiling) to the next multiple of the selected value.",
  floor:
    "The calculated amount will always be rounded down (floor) to the previous multiple of the selected value.",
};

const roundingRuleVerbs = {
  nearest: "rounded to nearest",
  ceiling: "rounded up to",
  floor: "rounded down to",
};

const roundingValueOptions = [
  { label: "1", value: 1 },
  { label: "5", value: 5 },
  { label: "10", value: 10 },
  { label: "100", value: 100 },
];

const sectionCardStyle = {
  p: 3,
  mb: 3,
  borderRadius: 2,
  border: `1px solid ${themes?.miliedLightGray}`,
  backgroundColor: themes?.whiteColor,
};

const sideNoteBoxStyle = {
  p: 2,
  borderRadius: 2,
  backgroundColor: "#f8f9fa",
  border: `1px solid ${themes?.miliedLightGray}`,
  height: "100%",
};

const infoNoteBoxStyle = {
  ...sideNoteBoxStyle,
  backgroundColor: "#eef4ff",
};

const SAMPLE_AMOUNT = 2356.47;
const SAMPLE_RATE = 698.923145;
const SAMPLE_HOURS = 9.816345;
const SAMPLE_CURRENCY_AMOUNT = 17473.0841;
const SAMPLE_PERCENTAGE = 12.5;

const roundToIncrement = (amount, rule, increment) => {
  if (rule === "none" || !increment) return amount;
  switch (rule) {
    case "ceiling":
      return Math.ceil(amount / increment) * increment;
    case "floor":
      return Math.floor(amount / increment) * increment;
    default:
      return Math.round(amount / increment) * increment;
  }
};

const formatAmount = (value) =>
  Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const GeneralConfiguration = () => {
  const { domain } = useSelector((state) => state.tokenReducer);
  const [loading, setLoading] = useState(false);
  const [configId, setConfigId] = useState(null);
  const [displayPrecision, setDisplayPrecision] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      rounding_rule: roundingRuleOptions[0],
      rounding_value: roundingValueOptions.find((option) => option.value === 5),
      calculation_precision: 6,
      rate_precision: 4,
      quantity_precision: 4,
      amount_precision: 2,
    },
  });

  useEffect(() => {
    const fetchConfiguration = async () => {
      const response = await customGetAPI(
        `${domain}payroll/setup/calculationsettings/calculation-settings/`
      );
      if (response.ok && response.responseDetails) {
        const payload = response.responseDetails;
        const data = Array.isArray(payload)
          ? payload[0]
          : Array.isArray(payload?.results)
          ? payload.results[0]
          : payload;
        setConfigId(data?.id ?? null);
        setDisplayPrecision(data?.display_precision ?? null);
        reset({
          rounding_rule:
            roundingRuleOptions.find(
              (option) => option.value === data?.rounding_rule
            ) || roundingRuleOptions[0],
          rounding_value:
            roundingValueOptions.find(
              (option) => option.value === parseFloat(data?.rounding_value)
            ) || roundingValueOptions.find((option) => option.value === 5),
          calculation_precision:
            data?.calculation_precision ?? 6,
          rate_precision: data?.rate_precision ?? 4,
          quantity_precision: data?.quantity_precision ?? 4,
          amount_precision: data?.amount_precision ?? 2,
        });
      }
    };
    fetchConfiguration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain]);

  const onSubmit = async (data) => {
    if (!configId) {
      showToast("error", "Calculation settings record could not be found.");
      return;
    }
    setLoading(true);
    const response = await customAPI(
      `${domain}payroll/setup/calculationsettings/calculation-settings/${configId}/`,
      "PUT",
      {
        rounding_rule: data.rounding_rule?.value,
        rounding_value: data.rounding_value?.value,
        calculation_precision: Number(
          data.calculation_precision
        ),
        display_precision:
          displayPrecision ?? Number(data.amount_precision),
        rate_precision: Number(data.rate_precision),
        quantity_precision: Number(data.quantity_precision),
        amount_precision: Number(data.amount_precision),
      }
    );
    setLoading(false);
    if (response.ok) {
      showToast("success", response?.responseDetails?.message);
    } else {
      showToast("error", response?.message);
    }
  };

  const selectedRoundingRule = watch("rounding_rule");
  const selectedRoundingValue = watch("rounding_value");
  const ratePrecision = watch("rate_precision");
  const quantityPrecision = watch("quantity_precision");
  const amountPrecision = watch("amount_precision");
  const isExactCalculation = selectedRoundingRule?.value === "none";

  const roundedAmount = roundToIncrement(
    SAMPLE_AMOUNT,
    selectedRoundingRule?.value,
    selectedRoundingValue?.value
  );

  const clampPrecision = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.max(0, Math.min(6, parsed)) : fallback;
  };

  const ratePrecisionValue = clampPrecision(ratePrecision, 4);
  const quantityPrecisionValue = clampPrecision(quantityPrecision, 4);
  const amountPrecisionValue = clampPrecision(amountPrecision, 2);

  const formattedRate = SAMPLE_RATE.toFixed(ratePrecisionValue);
  const formattedHours = SAMPLE_HOURS.toFixed(quantityPrecisionValue);
  const formattedCurrencyAmount = SAMPLE_CURRENCY_AMOUNT.toLocaleString(
    undefined,
    {
      minimumFractionDigits: amountPrecisionValue,
      maximumFractionDigits: amountPrecisionValue,
    }
  );
  const formattedPercentage = SAMPLE_PERCENTAGE.toFixed(2);

  return (
    <Box sx={{ pb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        Rounding Rules Configurations
      </Typography>
      <Typography {...infoStyle} sx={{ mb: 2 }}>
        Configure how salary calculations are performed across the payroll
        system.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Changes made here will apply only to future payroll calculations.
        Previously finalized payrolls are not recalculated.
      </Alert>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Paper elevation={0} sx={sectionCardStyle}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Percent fontSize="small" sx={{ color: themes?.blueColor }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Salary Rounding and Valuation
            </Typography>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 3,
              mb: 3,
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Rounding Rule</Typography>
              <Typography {...infoStyle}>
                Choose the rounding rule for salary calculations.
              </Typography>
              <Controller
                name="rounding_rule"
                control={control}
                rules={{ required: "Rounding rule is required" }}
                render={({ field }) => (
                  <CustomAutoComplete
                    dataList={roundingRuleOptions}
                    selectedValue={field.value}
                    updateDataFunction={(event, value) => field.onChange(value)}
                    placeHolder="Select rounding rule"
                    errorMessage={errors?.rounding_rule?.message || ""}
                    textBoxError={!!errors?.rounding_rule}
                  />
                )}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Rounding Value</Typography>
              <Typography {...infoStyle}>
                {isExactCalculation
                  ? "Not applicable when using Exact Calculation."
                  : "Choose the rounding value (increment) for salary calculations."}
              </Typography>
              <Controller
                name="rounding_value"
                control={control}
                rules={{
                  required: isExactCalculation ? false : "Rounding value is required",
                }}
                render={({ field }) => (
                  <CustomAutoComplete
                    dataList={roundingValueOptions}
                    selectedValue={field.value}
                    updateDataFunction={(event, value) => field.onChange(value)}
                    placeHolder="Select rounding value"
                    errorMessage={errors?.rounding_value?.message || ""}
                    textBoxError={!!errors?.rounding_value}
                    disableAction={isExactCalculation}
                  />
                )}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 3,
            }}
          >
            <Box sx={sideNoteBoxStyle}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <Visibility fontSize="small" sx={{ color: themes?.grayColor }} />
                <Typography sx={{ fontWeight: 700 }}>Preview</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Calculated Amount
                  </Typography>
                  <Typography sx={{ fontWeight: 700 }}>
                    {formatAmount(SAMPLE_AMOUNT)}
                  </Typography>
                </Box>
                <Typography color="text.secondary">→</Typography>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Rounded Amount
                  </Typography>
                  <Typography sx={{ fontWeight: 700, color: themes?.greenColor }}>
                    {formatAmount(roundedAmount)}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                {isExactCalculation
                  ? "Using Exact Calculation — no rounding applied"
                  : `Using ${selectedRoundingRule?.label || "-"} with value ${
                      selectedRoundingValue?.label || "-"
                    }`}
              </Typography>
            </Box>

            <Box sx={infoNoteBoxStyle}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <InfoOutlined fontSize="small" sx={{ color: themes?.blueColor }} />
                <Typography sx={{ fontWeight: 700 }}>About Rounding</Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {roundingRuleDescriptions[selectedRoundingRule?.value] ||
                  roundingRuleDescriptions.none}
              </Typography>
              {!isExactCalculation && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Example: {formatAmount(SAMPLE_AMOUNT)}{" "}
                  {roundingRuleVerbs[selectedRoundingRule?.value] ||
                    roundingRuleVerbs.nearest}{" "}
                  {selectedRoundingValue?.label || "-"} ={" "}
                  {formatAmount(roundedAmount)}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                This rounding rule affects payrun cycle calculations only.
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 2fr" },
            gap: 3,
            alignItems: "stretch",
          }}
        >
        <Paper elevation={0} sx={sectionCardStyle}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Calculate fontSize="small" sx={{ color: themes?.blueColor }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Calculation Precision
            </Typography>
          </Box>
          <Typography {...infoStyle}>
            Controls the precision used for all internal calculations.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography sx={{ fontWeight: 600 }}>
                Internal Calculation Precision
              </Typography>
              <Tooltip title="Number of decimal places used in all internal calculations.">
                <InfoOutlined fontSize="inherit" sx={{ color: themes?.grayColor }} />
              </Tooltip>
            </Box>
            <Typography {...infoStyle} noWrap sx={{ mb: 1 }}>
              Decimals used for internal calculations.
            </Typography>
            <Controller
              name="calculation_precision"
              control={control}
              rules={{
                required: "Internal calculation precision is required",
                min: { value: 0, message: "Minimum value is 0" },
                max: { value: 6, message: "Maximum value is 6" },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  size="small"
                  fullWidth
                  inputProps={{ min: 0, max: 6, step: 1 }}
                  error={!!errors?.calculation_precision}
                  sx={{ backgroundColor: themes?.whiteColor, borderRadius: 1 }}
                />
              )}
            />
            {errors?.calculation_precision?.message && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                {errors.calculation_precision.message}
              </Typography>
            )}
            <Box sx={{ display: "flex", gap: 4, mt: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                <CheckCircle fontSize="small" sx={{ color: themes?.greenColor, mt: "2px" }} />
                <Box>
                  <Typography variant="caption" sx={{ display: "block", fontWeight: 600 }}>
                    System Default
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    6 decimals (Recommended)
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ display: "block", fontWeight: 600 }}>
                  Used For
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  All calculations
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        <Paper elevation={0} sx={sectionCardStyle}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Visibility fontSize="small" sx={{ color: themes?.blueColor }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Display Precision
            </Typography>
          </Box>
          <Typography {...infoStyle}>
            Controls how values are displayed on payslips, reports and screens.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", xl: "1fr 1fr 1fr" },
              gap: 3,
              mb: 3,
            }}
          >
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography sx={{ fontWeight: 600 }}>Rate Precision</Typography>
                <Tooltip title="Number of decimal places for rates and percentages.">
                  <InfoOutlined fontSize="inherit" sx={{ color: themes?.grayColor }} />
                </Tooltip>
              </Box>
              <Typography {...infoStyle} noWrap sx={{ mb: 1 }}>
                Decimals for rates & percentages.
              </Typography>
              <Controller
                name="rate_precision"
                control={control}
                rules={{
                  required: "Rate precision is required",
                  min: { value: 0, message: "Minimum value is 0" },
                  max: { value: 6, message: "Maximum value is 6" },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    size="small"
                    fullWidth
                    inputProps={{ min: 0, max: 6, step: 1 }}
                    error={!!errors?.rate_precision}
                    sx={{ backgroundColor: themes?.whiteColor, borderRadius: 1 }}
                  />
                )}
              />
              {errors?.rate_precision?.message && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                  {errors.rate_precision.message}
                </Typography>
              )}
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mt: 1.5 }}>
                <CheckCircle fontSize="small" sx={{ color: themes?.greenColor, mt: "2px" }} />
                <Box>
                  <Typography variant="caption" sx={{ display: "block", fontWeight: 600 }}>
                    System Default
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    4 decimals (Recommended)
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography sx={{ fontWeight: 600 }}>Quantity Precision</Typography>
                <Tooltip title="Number of decimal places for hours, days and other quantities.">
                  <InfoOutlined fontSize="inherit" sx={{ color: themes?.grayColor }} />
                </Tooltip>
              </Box>
              <Typography {...infoStyle} noWrap sx={{ mb: 1 }}>
                Decimals for hours, days & qty.
              </Typography>
              <Controller
                name="quantity_precision"
                control={control}
                rules={{
                  required: "Quantity precision is required",
                  min: { value: 0, message: "Minimum value is 0" },
                  max: { value: 6, message: "Maximum value is 6" },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    size="small"
                    fullWidth
                    inputProps={{ min: 0, max: 6, step: 1 }}
                    error={!!errors?.quantity_precision}
                    sx={{ backgroundColor: themes?.whiteColor, borderRadius: 1 }}
                  />
                )}
              />
              {errors?.quantity_precision?.message && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                  {errors.quantity_precision.message}
                </Typography>
              )}
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mt: 1.5 }}>
                <CheckCircle fontSize="small" sx={{ color: themes?.greenColor, mt: "2px" }} />
                <Box>
                  <Typography variant="caption" sx={{ display: "block", fontWeight: 600 }}>
                    System Default
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    4 decimals (Recommended)
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography sx={{ fontWeight: 600 }}>Amount Precision</Typography>
                <Tooltip title="Number of decimal places for currency amounts such as salary, deductions, taxes and net pay.">
                  <InfoOutlined fontSize="inherit" sx={{ color: themes?.grayColor }} />
                </Tooltip>
              </Box>
              <Typography {...infoStyle} noWrap sx={{ mb: 1 }}>
                Decimals for currency amounts.
              </Typography>
              <Controller
                name="amount_precision"
                control={control}
                rules={{
                  required: "Amount precision is required",
                  min: { value: 0, message: "Minimum value is 0" },
                  max: { value: 2, message: "Maximum value is 2" },
                  validate: (value) =>
                    [0, 1, 2].includes(Number(value)) ||
                    "Amount precision must be 0, 1, or 2",
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    size="small"
                    fullWidth
                    inputProps={{ min: 0, max: 2, step: 1 }}
                    error={!!errors?.amount_precision}
                    sx={{ backgroundColor: themes?.whiteColor, borderRadius: 1 }}
                  />
                )}
              />
              {errors?.amount_precision?.message && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                  {errors.amount_precision.message}
                </Typography>
              )}
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mt: 1.5 }}>
                <CheckCircle fontSize="small" sx={{ color: themes?.greenColor, mt: "2px" }} />
                <Box>
                  <Typography variant="caption" sx={{ display: "block", fontWeight: 600 }}>
                    System Default
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    2 decimals (Recommended)
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>
        </Box>

        <Box
          sx={{
            ...infoNoteBoxStyle,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" },
            gap: 3,
            mb: 3,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <InfoOutlined fontSize="small" sx={{ color: themes?.blueColor }} />
              <Typography sx={{ fontWeight: 700 }}>
                How Display Precision Works
              </Typography>
            </Box>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              <Typography component="li" variant="body2">
                Rates and percentages are displayed using Rate Precision.
              </Typography>
              <Typography component="li" variant="body2">
                Hours, days and quantities are displayed using Quantity Precision.
              </Typography>
              <Typography component="li" variant="body2">
                Salary amounts, deductions, taxes and net pay are displayed
                using Amount Precision.
              </Typography>
            </Box>
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>Examples</Typography>
            <Typography variant="body2">Rate: {formattedRate}</Typography>
            <Typography variant="body2">Hours: {formattedHours}</Typography>
            <Typography variant="body2">
              Amount: ₹{formattedCurrencyAmount}
            </Typography>
            <Typography variant="body2">
              Percentage: {formattedPercentage}%
            </Typography>
          </Box>
        </Box>

        <Box sx={{ ...toAlignEnd, mt: 3 }}>
          <CustomButtonWithBorder actionName="Cancel" typeName="button" />
          <CustomButton
            actionName="Save Changes"
            typeName="submit"
            disableAction={loading}
          />
        </Box>
      </form>
    </Box>
  );
};
